import { orderBy } from "lodash";
import moment from "moment";
import { ReactElement, useState } from "react";
import { Card, Modal } from "react-bootstrap";
import { AiOutlineClose } from "react-icons/ai";
import { FaEye } from "react-icons/fa";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface History {
  date: string;
  timestamp: number;
  value: number;
}

export interface RenderableData {
  score: number;
  name: string;
  usedMetrics: string[];
  icon: ReactElement;
  historic: History[];
}
export default function ScoreMicroCard({
  score,
  name,
  usedMetrics,
  icon,
  historic,
}: RenderableData) {
  const [showModal, setShowModal] = useState<boolean>(false);

  const color = getColor(score);
  return (
    <Card
      style={{
        borderRadius: 15,
        border: "1px solid",
        boxShadow: "0 0 10px rgba(113, 110, 112, 0.24)",
      }}
      className={`health-card border-${color}`}
      onClick={() => {
        if (!showModal) {
          setShowModal(true);
        }
      }}
    >
      {getDetailModal(
        showModal,
        () => setShowModal(false),
        usedMetrics,
        historic
      )}
      <FaEye
        className="cursor-pointer"
        style={{ position: "absolute", right: 10, top: 10 }}
        size={15}
        onClick={() => setShowModal(true)}
      />
      <div className="pd-10 mg-t-10">
        <div
          className="d-flex justify-content-center text-center"
          style={{ flexDirection: "column" }}
        >
          <div
            className="text-center"
            style={{
              marginBottom: "0.5rem",
            }}
          >
            {icon}
          </div>
          <h6>{name}</h6>
          <p className={`tx-22 tx-${color} tx-medium`}>
            {score?.toFixed(1).replace(".", ",")}
            <span
              className="tx-12 mg-b-0 tx-light"
              style={{ color: "var(--text-2)" }}
            >
              /10
            </span>
          </p>
          <span className="text-center tx-10" style={{ marginTop: "-0.5rem" }}>
            Com base em {usedMetrics.length} métricas
          </span>
        </div>
      </div>
    </Card>
  );
}

function getDetailModal(
  show: boolean,
  closeFunction: CallableFunction,
  usedMetrics: string[],
  historics: History[]
) {
  let data: any[] = [];

  if (historics.length > 0) {
    data = orderBy(historics, ["timestamp"], "asc").map((historic, i) => {
      return {
        year: moment(historic.timestamp).format("DD/MM/YYYY"),
        Nota: historic.value.toFixed(1),
      };
    });
  }

  return (
    <Modal
      show={show}
      onExit={() => closeFunction()}
      onHide={() => closeFunction()}
      style={{
        overflowY: "auto",
        maxHeight: "98vh",
      }}
    >
      <Modal.Header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "var(--bg-1)",
        }}
      >
        <Modal.Title>Detalhes da nota</Modal.Title>
        <AiOutlineClose
          onClick={() => closeFunction()}
          size={23}
          className="float-right cursor-pointer"
        />
      </Modal.Header>

      <Modal.Body>
        <h5>Métricas usadas</h5>
        <p>
          Abaixo, você consegue ver todas as métricas usadas para determinar a
          nota final gerada por nossa IA.
        </p>

        <Card>
          <div
            style={{
              maxHeight: "10rem",
              overflow: "auto",
              padding: "1rem",
            }}
          >
            <div className="table-responsive">
              <table className="table table-hover mg-b-0">
                <thead>
                  <tr>
                    <th>Nome da métrica</th>
                  </tr>
                </thead>
                <tbody>
                  {usedMetrics.map((metric, i) => {
                    return (
                      <tr key={i}>
                        <td>{metric}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Card>

        <h5 className="mg-t-20">Historico</h5>
        <p>
          Abaixo, você consegue ver o historico de notas que já foram geradas
          por nossa IA anteriormente.
        </p>

        <div className="pd-0 mg-r-20">
          <ResponsiveContainer width={"100%"} height={300}>
            <LineChart
              width={500}
              height={300}
              data={data}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="1 1" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip
                contentStyle={{
                  borderRadius: 10,
                  backgroundColor: "var(--bg-10)",
                  borderColor: "var(--border)",
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="Nota"
                stroke="#8884d8"
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function getColor(score: number): string {
  if (score >= 8) {
    return "success";
  } else if (score <= 4) {
    return "danger";
  } else {
    return "warning";
  }
}
